import { Trans, t } from "@lingui/macro";
import { PlusCircle, MinusCircle, Loader2, RefreshCw, Layers } from "lucide-react";
import React, { useEffect, useReducer, useState } from "react";
import { useAppUIContext } from "../../context/AppUIContext";
import { RoomSimplified } from "../../models/Room";
import { RoomItem } from "../../models/RoomItem";
import { Soribot } from "../../models/Soribot";
import { SourceRoomItem } from "../../models/SoribotSource";
import { Workspace } from "../../models/Workspace";
import { Fire } from "../../services/FireClient";
import { My } from "../../services/My";
import { UtilApp } from "../../services/Utils/UtilApp";
import { getSourceInfo } from "./SoribotDatasources";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion";
import { Button } from "../../components/ui/button";
import { useToast } from "../../hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

export const SoribotDatasourcesMemos: React.FC<{ bot: Soribot }> = ({ bot }) => {
  const [inProgress, setInProgress] = useState(false);
  const [inProgressUpdate, setInProgressUpdate] = useState(false);
  const { toast } = useToast();
  const [sourceItemsMap] = useState<Map<string, SourceRoomItem[]>>(new Map());
  const [removedSourceItems, setRemovedSourceItems] = useState<SourceRoomItem[]>([]);
  const [rooms, setRooms] = useState<RoomSimplified[]>();
  const { workspaces } = useAppUIContext();
  const [_, forceUpdate] = useReducer(x => x + 1, 0);
  const workspace = workspaces.find(w => w.id === bot.workspaceId);
  const [accordionValue, setAccordionValue] = useState<string | undefined>(undefined);
  const [alert, setAlert] = useState<{ open: boolean, title: string, message: React.ReactNode, confirm: (() => void) | null }>({ open: false, title: '', message: '', confirm: null });

  useEffect(() => {
    setInProgress(true);
    return Fire.bot.subscribeToSourceObjects(bot.id, 'room-item', (list: SourceRoomItem[]) => {
      sourceItemsMap.clear();
      list.forEach(si => {
        if (!sourceItemsMap.has(si.item.roomId)) {
          sourceItemsMap.set(si.item.roomId, []);
        }
        const items = sourceItemsMap.get(si.item.roomId);
        items?.push(si);
      });
      setInProgress(false);
      forceUpdate();
    });
  }, [bot.id]);

  function getSourceItemList() {
    return Array.from(sourceItemsMap.keys()).map(roomId => {
      const items = sourceItemsMap.get(roomId);
      if (items && items.length > 0) {
        return <div key={roomId} className="space-y-1">
          <div className="font-bold text-sm px-2 mt-4 text-gray-700"># {items[0].roomName || t`Rooms`}</div>
          {items.map(si => SourceItem(si))}
        </div>
      } else {
        return null;
      }
    })
  }

  return <>
    <Accordion type="single" collapsible value={accordionValue} onValueChange={setAccordionValue} className="w-full bg-white rounded border mb-4">
      <AccordionItem value="add-memos" className="border-none">
        <AccordionTrigger className="px-4 hover:no-underline" onClick={loadRooms}>
          <div className="flex items-center text-sm font-semibold"><PlusCircle className="w-4 h-4 mr-2" /> <Trans>Add memos from your rooms</Trans></div>
        </AccordionTrigger>
        <AccordionContent className="px-4 pb-4">
          <div className="p-4 border rounded bg-gray-50 text-left space-y-4">
            <div className="font-bold text-gray-500 text-xs uppercase"><Trans>Rooms you have joined in workspace {workspace?.name ?? ''}</Trans></div>
            <div className="space-y-2">
              {rooms?.map(r => <DataRoomListItem key={r.id} soribotId={bot.id} room={r} ws={workspace} sourceItems={sourceItemsMap.get(r.id) ?? []} />)}
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>

    {inProgress && <div className="p-4 flex justify-center"><Loader2 className="animate-spin text-gray-400" /></div>}
    {!inProgress && <>
      <h4 className="text-sm font-bold text-gray-500 px-2 mt-4 uppercase"><Trans>Added memos</Trans></h4>
      {sourceItemsMap.size === 0 && <div className="p-4 text-center text-gray-500"><Trans>No memos added</Trans></div>}
      {getSourceItemList()}
      <br />
      <div className="px-2">
        <Button disabled={inProgressUpdate} onClick={updateBotContent}><RefreshCw className={`w-4 h-4 mr-2 ${inProgressUpdate ? 'animate-spin' : ''}`} /><Trans>Sync bot knowledge</Trans></Button>
      </div>
    </>}

    <AlertDialog open={alert.open} onOpenChange={(open) => setAlert(prev => ({ ...prev, open }))}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{alert.title}</AlertDialogTitle>
          <AlertDialogDescription>{alert.message}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          {alert.confirm ?
            <>
              <AlertDialogCancel><Trans>Cancel</Trans></AlertDialogCancel>
              <AlertDialogAction onClick={() => { alert.confirm?.(); setAlert(prev => ({ ...prev, open: false })) }}><Trans>Confirm</Trans></AlertDialogAction>
            </>
            :
            <AlertDialogAction onClick={() => setAlert(prev => ({ ...prev, open: false }))}><Trans>OK</Trans></AlertDialogAction>
          }
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>;

  function SourceItem(si: SourceRoomItem) {
    const toRemove = removedSourceItems.some(b => b.id === si.id);
    return <div key={si.id} className="flex items-center justify-between p-3 rounded border hover:bg-gray-50 bg-white">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 font-medium truncate text-sm">
          <Layers className="w-4 h-4 text-indigo-500 shrink-0" />
          <span className="truncate">{si.title}</span>
        </div>
        <div className="text-xs text-gray-500 mt-1 truncate">{getSourceInfo(toRemove, si)}</div>
      </div>
      {inProgressUpdate ?
        <Loader2 className="w-5 h-5 animate-spin text-gray-400 shrink-0" />
        :
        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0" onClick={() => removeSourceItem(si)}>
          <MinusCircle className="w-5 h-5" />
        </Button>
      }
    </div>;
  }

  async function loadRooms() {
    if (rooms === undefined) {
      setInProgress(true);
      try {
        const r = await Fire.ws.getJoinedWorkspaceRooms({ workspaceId: bot.workspaceId });
        setRooms(r.rooms.sort((a, b) => a.name.localeCompare(b.name)))
      } catch (error: any) {
        toast({ variant: "destructive", title: t`Oops, could not fetch the rooms. ${error?.message || ''}` });
      }
      setInProgress(false);
    }
  }

  async function removeSourceItem(si: SourceRoomItem) {
    setRemovedSourceItems([...removedSourceItems, si]);
  }

  async function updateBotContent() {
    setInProgressUpdate(true);
    await Fire.bot.deleteSources(removedSourceItems);
    await Fire.bot.updateSoribotContent({ soribotId: bot.id, type: 'room-item' });
    setAlert({ open: true, title: t`Done!`, message: t`The knowledge content is updated. The result can be viewed in the Knowledge viewer.`, confirm: null });
    setRemovedSourceItems([]);
    setInProgressUpdate(false);
  }
}

// ===========================
// DataRoomListItem
// ===========================

const DataRoomListItem: React.FC<{ soribotId: string, room: RoomSimplified, ws: Workspace | undefined, sourceItems: SourceRoomItem[] }> = ({ soribotId, room, ws, sourceItems }) => {
  const [inProgress, setInProgress] = useState(false);
  const [inProgressAddItemId, setInProgressAddItemId] = useState('');
  const [items, setItems] = useState<RoomItem[]>();
  const [accordionValue, setAccordionValue] = useState<string | undefined>(undefined);

  return <Accordion type="single" collapsible value={accordionValue} onValueChange={setAccordionValue} className="w-full bg-white rounded border">
    <AccordionItem value="room-items" className="border-none">
      <AccordionTrigger className="px-3 py-2 hover:no-underline text-sm font-medium" onClick={loadRoomItems}>
        <div className="truncate"># {room.name}</div>
      </AccordionTrigger>
      <AccordionContent className="border-t">
        {getContent()}
      </AccordionContent>
    </AccordionItem>
  </Accordion>

  function getContent() {
    return <div className="divide-y">
      {inProgress && <div className="p-2 flex justify-center"><Loader2 className="animate-spin h-4 w-4" /></div>}
      {items?.map(item => roomItem(item))}
      {(items?.length === 0) && <div className="p-3 text-xs text-gray-500"><Trans>No memos in this room</Trans></div>}
    </div>
  }

  function roomItem(item: RoomItem) {
    const title = item.title ?? item.text ?? item.url;
    const isAdded = sourceItems.some(si => si.item.id === item.id);

    return <div key={item.id} className={`flex items-start justify-between p-3 gap-3 ${isAdded ? 'opacity-50 pointer-events-none bg-gray-50' : 'hover:bg-gray-50'}`}>
      <div className="flex-1 min-w-0 text-sm">
        <div className="font-medium truncate flex items-center gap-2"><Layers className="w-3 h-3 text-indigo-500 shrink-0" /> {title}</div>
        <div className="text-gray-500 text-xs mt-1 line-clamp-2" dangerouslySetInnerHTML={{ __html: UtilApp.sanitizedHTML(item.text ?? '') }} />
      </div>
      {inProgressAddItemId === item.id ?
        <Loader2 className="w-4 h-4 animate-spin text-gray-400 shrink-0" />
        :
        <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={() => addRoomItemAsSourceItem(item)} disabled={isAdded}>
          <PlusCircle className="w-4 h-4 text-blue-500" />
        </Button>
      }
    </div>;
  }

  function loadRoomItems(): void {
    if (items === undefined) {
      setInProgress(true);
      Fire.item.getRoomItems(room.id, ['memo']).then(list => {
        setItems(list);
        setInProgress(false);
      });
    }
  }
  async function addRoomItemAsSourceItem(item: RoomItem) {
    setInProgressAddItemId(item.id);
    const obj = new SourceRoomItem(soribotId, My.user.id, item, room.name);
    await Fire.bot.saveSourceObj(obj);
    await Fire.item.setIsDataSource(item, true);
    setInProgressAddItemId('');
  }
}
